/**
 * Create / edit investor profile.
 * Supports edit via `?id={id}` query param — same form is reused.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import investorService from '../services/investorService';
import {
  Investor,
  InvestorType,
  Stage,
  AccreditationStatus,
  RelationshipStatus,
  DDStyle,
  Instrument,
  DecisionTimeline,
  PostInvestmentInvolvement,
  RiskTolerance,
} from '../types/investor';

const INVESTOR_TYPE_OPTIONS: Array<{ value: InvestorType; label: string }> = [
  { value: 'angel', label: 'Angel' },
  { value: 'super_angel', label: 'Super Angel' },
  { value: 'micro_vc', label: 'Micro VC' },
  { value: 'traditional_vc', label: 'Traditional VC' },
  { value: 'family_office', label: 'Family Office' },
  { value: 'corporate_strategic', label: 'Corporate Strategic' },
  { value: 'hnw_individual', label: 'HNW Individual' },
  { value: 'syndicate_lead', label: 'Syndicate Lead' },
  { value: 'lp_fof', label: 'LP / Fund of Funds' },
  { value: 'lp_institutional', label: 'LP — Institutional (pension/endowment/SWF)' },
  { value: 'lp_individual', label: 'LP — Individual / HNW' },
  { value: 'philanthropy', label: 'Philanthropy / Foundation' },
  { value: 'corporate_philanthropy', label: 'Corporate Philanthropy' },
];

const STAGE_OPTIONS: Array<{ value: Stage; label: string }> = [
  { value: 'pre_seed', label: 'Pre-Seed' },
  { value: 'seed', label: 'Seed' },
  { value: 'series_a', label: 'Series A' },
  { value: 'series_b', label: 'Series B' },
  { value: 'series_c', label: 'Series C' },
  { value: 'growth', label: 'Growth' },
  { value: 'late_stage', label: 'Late Stage' },
];

const ACCREDITATION_OPTIONS: Array<{ value: AccreditationStatus; label: string }> = [
  { value: 'confirmed_accredited', label: 'Confirmed Accredited' },
  { value: 'likely_high', label: 'Likely (High)' },
  { value: 'likely_medium', label: 'Likely (Medium)' },
  { value: 'unknown', label: 'Unknown' },
  { value: 'non_accredited', label: 'Non-Accredited' },
];

const RELATIONSHIP_OPTIONS: Array<{ value: RelationshipStatus; label: string }> = [
  { value: 'cold', label: 'Cold' },
  { value: 'warm', label: 'Warm' },
  { value: 'active', label: 'Active' },
  { value: 'closed', label: 'Closed' },
  { value: 'dormant', label: 'Dormant' },
];

const DD_STYLE_OPTIONS: Array<{ value: DDStyle; label: string }> = [
  { value: 'financial_heavy', label: 'Financial Heavy' },
  { value: 'product_demo', label: 'Product Demo' },
  { value: 'team_first', label: 'Team First' },
  { value: 'thesis_driven', label: 'Thesis Driven' },
  { value: 'data_driven', label: 'Data Driven' },
];

const INSTRUMENT_OPTIONS: Array<{ value: Instrument; label: string }> = [
  { value: 'safe', label: 'SAFE' },
  { value: 'convertible_note', label: 'Convertible Note' },
  { value: 'priced_equity', label: 'Priced Equity' },
  { value: 'revenue_based', label: 'Revenue-Based' },
  { value: 'debt', label: 'Debt' },
  { value: 'spv', label: 'SPV' },
  { value: 'warrant', label: 'Warrant' },
];

const DECISION_TIMELINE_OPTIONS: Array<{ value: DecisionTimeline; label: string }> = [
  { value: 'fast_mover', label: 'Fast Mover' },
  { value: 'standard', label: 'Standard' },
  { value: 'committee', label: 'Committee' },
  { value: 'slow', label: 'Slow' },
];

const POST_INVESTMENT_OPTIONS: Array<{ value: PostInvestmentInvolvement; label: string }> = [
  { value: 'board_seat', label: 'Board Seat' },
  { value: 'advisory', label: 'Advisory' },
  { value: 'hands_on', label: 'Hands On' },
  { value: 'silent', label: 'Silent' },
];

const RISK_TOLERANCE_OPTIONS: Array<{ value: RiskTolerance; label: string }> = [
  { value: 'conservative', label: 'Conservative' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'aggressive', label: 'Aggressive' },
  { value: 'unknown', label: 'Unknown' },
];

interface FormState {
  // Identity
  full_name: string;
  firm_name: string;
  title: string;
  location_city: string;
  location_state: string;
  location_country: string;
  linkedin_url: string;
  website_url: string;
  email: string;
  phone: string;

  // Classification
  investor_type: InvestorType | '';
  stage_preferences: Stage[];
  sector_focus_csv: string;
  geographic_preference_csv: string;

  // Accreditation
  accreditation_status: AccreditationStatus | '';
  accreditation_confidence: string;

  // Financial
  check_size_min: string;
  check_size_max: string;
  estimated_aum: string;
  fund_size: string;

  // DD Profile
  dd_style: DDStyle | '';
  preferred_instruments: Instrument[];
  decision_timeline: DecisionTimeline | '';
  post_investment_involvement: PostInvestmentInvolvement | '';
  risk_tolerance: RiskTolerance | '';

  // Intelligence
  investment_philosophy: string;
  known_red_flags: string;
  known_interests: string;
  negotiation_style: string;
  sentiment_summary: string;

  // Relationship
  relationship_status: RelationshipStatus | '';
  tags_csv: string;
  notes: string;
}

const emptyForm: FormState = {
  full_name: '',
  firm_name: '',
  title: '',
  location_city: '',
  location_state: '',
  location_country: 'US',
  linkedin_url: '',
  website_url: '',
  email: '',
  phone: '',
  investor_type: '',
  stage_preferences: [],
  sector_focus_csv: '',
  geographic_preference_csv: '',
  accreditation_status: 'unknown',
  accreditation_confidence: '',
  check_size_min: '',
  check_size_max: '',
  estimated_aum: '',
  fund_size: '',
  dd_style: '',
  preferred_instruments: [],
  decision_timeline: '',
  post_investment_involvement: '',
  risk_tolerance: '',
  investment_philosophy: '',
  known_red_flags: '',
  known_interests: '',
  negotiation_style: '',
  sentiment_summary: '',
  relationship_status: 'cold',
  tags_csv: '',
  notes: '',
};

const csvToArray = (value: string): string[] =>
  value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const arrayToCsv = (value?: string[]): string => (value ? value.join(', ') : '');

const toNumber = (value: string): number | undefined => {
  if (value === '' || value === undefined) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const toEnum = <T extends string>(value: T | ''): T | undefined =>
  value === '' ? undefined : value;

const investorToForm = (investor: Investor): FormState => ({
  full_name: investor.full_name || '',
  firm_name: investor.firm_name || '',
  title: investor.title || '',
  location_city: investor.location_city || '',
  location_state: investor.location_state || '',
  location_country: investor.location_country || 'US',
  linkedin_url: investor.linkedin_url || '',
  website_url: investor.website_url || '',
  email: investor.email || '',
  phone: investor.phone || '',
  investor_type: investor.investor_type || '',
  stage_preferences: investor.stage_preferences || [],
  sector_focus_csv: arrayToCsv(investor.sector_focus),
  geographic_preference_csv: arrayToCsv(investor.geographic_preference),
  accreditation_status: investor.accreditation_status || 'unknown',
  accreditation_confidence:
    investor.accreditation_confidence !== undefined
      ? String(investor.accreditation_confidence)
      : '',
  check_size_min:
    investor.check_size_min !== undefined ? String(investor.check_size_min) : '',
  check_size_max:
    investor.check_size_max !== undefined ? String(investor.check_size_max) : '',
  estimated_aum:
    investor.estimated_aum !== undefined ? String(investor.estimated_aum) : '',
  fund_size: investor.fund_size !== undefined ? String(investor.fund_size) : '',
  dd_style: investor.dd_style || '',
  preferred_instruments: investor.preferred_instruments || [],
  decision_timeline: investor.decision_timeline || '',
  post_investment_involvement: investor.post_investment_involvement || '',
  risk_tolerance: investor.risk_tolerance || '',
  investment_philosophy: investor.investment_philosophy || '',
  known_red_flags: investor.known_red_flags || '',
  known_interests: investor.known_interests || '',
  negotiation_style: investor.negotiation_style || '',
  sentiment_summary: investor.sentiment_summary || '',
  relationship_status: investor.relationship_status || 'cold',
  tags_csv: arrayToCsv(investor.tags),
  notes: investor.notes || '',
});

const formToPayload = (form: FormState): Partial<Investor> => ({
  full_name: form.full_name.trim(),
  firm_name: form.firm_name.trim() || undefined,
  title: form.title.trim() || undefined,
  location_city: form.location_city.trim() || undefined,
  location_state: form.location_state.trim() || undefined,
  location_country: form.location_country.trim() || undefined,
  linkedin_url: form.linkedin_url.trim() || undefined,
  website_url: form.website_url.trim() || undefined,
  email: form.email.trim() || undefined,
  phone: form.phone.trim() || undefined,
  investor_type: toEnum(form.investor_type),
  stage_preferences: form.stage_preferences.length ? form.stage_preferences : undefined,
  sector_focus: csvToArray(form.sector_focus_csv).length
    ? csvToArray(form.sector_focus_csv)
    : undefined,
  geographic_preference: csvToArray(form.geographic_preference_csv).length
    ? csvToArray(form.geographic_preference_csv)
    : undefined,
  accreditation_status: toEnum(form.accreditation_status),
  accreditation_confidence: toNumber(form.accreditation_confidence),
  check_size_min: toNumber(form.check_size_min),
  check_size_max: toNumber(form.check_size_max),
  estimated_aum: toNumber(form.estimated_aum),
  fund_size: toNumber(form.fund_size),
  dd_style: toEnum(form.dd_style),
  preferred_instruments: form.preferred_instruments.length
    ? form.preferred_instruments
    : undefined,
  decision_timeline: toEnum(form.decision_timeline),
  post_investment_involvement: toEnum(form.post_investment_involvement),
  risk_tolerance: toEnum(form.risk_tolerance),
  investment_philosophy: form.investment_philosophy.trim() || undefined,
  known_red_flags: form.known_red_flags.trim() || undefined,
  known_interests: form.known_interests.trim() || undefined,
  negotiation_style: form.negotiation_style.trim() || undefined,
  sentiment_summary: form.sentiment_summary.trim() || undefined,
  relationship_status: toEnum(form.relationship_status),
  tags: csvToArray(form.tags_csv).length ? csvToArray(form.tags_csv) : undefined,
  notes: form.notes.trim() || undefined,
});

interface MultiSelectChipProps<T extends string> {
  options: Array<{ value: T; label: string }>;
  selected: T[];
  onChange: (next: T[]) => void;
}

function MultiSelectChips<T extends string>({
  options,
  selected,
  onChange,
}: MultiSelectChipProps<T>) {
  const toggle = (value: T) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = selected.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggle(opt.value)}
            className={`px-3 py-1 text-xs rounded-full border transition-colors ${
              active
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    {children}
  </section>
);

const labelClass = 'block text-xs font-medium text-gray-700 mb-1';
const inputClass =
  'block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500';

const NewInvestorPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const investorId = editId ? Number(editId) : null;
  const isEditMode = investorId !== null && !Number.isNaN(investorId);

  const [form, setForm] = useState<FormState>(emptyForm);
  const [isLoading, setIsLoading] = useState<boolean>(isEditMode);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (!isEditMode || investorId === null) return;
    const loadData = async () => {
      setIsLoading(true);
      const result = await investorService.fetchInvestor(investorId);
      if (result.error) {
        toast.error(result.error);
      } else if (result.data) {
        setForm(investorToForm(result.data));
      }
      setIsLoading(false);
    };
    loadData();
  }, [isEditMode, investorId]);

  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.full_name.trim()) {
      toast.error('Full name is required');
      return;
    }
    setIsSubmitting(true);
    const payload = formToPayload(form);
    const result =
      isEditMode && investorId !== null
        ? await investorService.updateInvestor(investorId, payload)
        : await investorService.createInvestor(payload);
    setIsSubmitting(false);
    if (result.error || !result.data) {
      toast.error(result.error || 'Failed to save investor');
      return;
    }
    toast.success(isEditMode ? 'Investor updated' : 'Investor created');
    navigate(`/investors/${result.data.id}`);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
          <Loader2 className="w-5 h-5 mr-3 animate-spin" />
          Loading investor...
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <Link
            to={isEditMode && investorId !== null ? `/investors/${investorId}` : '/investors'}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-2"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Cancel
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Edit investor' : 'Add investor'}
          </h1>
          <p className="text-gray-600 mt-1">
            Capture the profile fields needed for matching, dossier generation, and
            relationship tracking.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Identity */}
          <Section title="Identity">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Full name *</label>
                <input
                  type="text"
                  required
                  value={form.full_name}
                  onChange={(e) => updateField('full_name', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Firm name</label>
                <input
                  type="text"
                  value={form.firm_name}
                  onChange={(e) => updateField('firm_name', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Title</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Phone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>LinkedIn URL</label>
                <input
                  type="url"
                  value={form.linkedin_url}
                  onChange={(e) => updateField('linkedin_url', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Website URL</label>
                <input
                  type="url"
                  value={form.website_url}
                  onChange={(e) => updateField('website_url', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>City</label>
                <input
                  type="text"
                  value={form.location_city}
                  onChange={(e) => updateField('location_city', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>State / Region</label>
                <input
                  type="text"
                  value={form.location_state}
                  onChange={(e) => updateField('location_state', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Country</label>
                <input
                  type="text"
                  value={form.location_country}
                  onChange={(e) => updateField('location_country', e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>
          </Section>

          {/* Classification */}
          <Section title="Classification">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Investor type</label>
                <select
                  value={form.investor_type}
                  onChange={(e) =>
                    updateField('investor_type', e.target.value as InvestorType | '')
                  }
                  className={inputClass}
                >
                  <option value="">— Select —</option>
                  {INVESTOR_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Sector focus (comma-separated)</label>
                <input
                  type="text"
                  placeholder="fintech, healthtech, climate"
                  value={form.sector_focus_csv}
                  onChange={(e) => updateField('sector_focus_csv', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Geographic preference (comma-separated)</label>
                <input
                  type="text"
                  placeholder="US, EU, LATAM"
                  value={form.geographic_preference_csv}
                  onChange={(e) =>
                    updateField('geographic_preference_csv', e.target.value)
                  }
                  className={inputClass}
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Stage preferences</label>
              <MultiSelectChips
                options={STAGE_OPTIONS}
                selected={form.stage_preferences}
                onChange={(next) => updateField('stage_preferences', next)}
              />
            </div>
          </Section>

          {/* Accreditation */}
          <Section title="Accreditation">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Accreditation status</label>
                <select
                  value={form.accreditation_status}
                  onChange={(e) =>
                    updateField(
                      'accreditation_status',
                      e.target.value as AccreditationStatus | ''
                    )
                  }
                  className={inputClass}
                >
                  <option value="">— Select —</option>
                  {ACCREDITATION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Confidence (0–100)</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={form.accreditation_confidence}
                  onChange={(e) =>
                    updateField('accreditation_confidence', e.target.value)
                  }
                  className={inputClass}
                />
              </div>
            </div>
          </Section>

          {/* Financial */}
          <Section title="Financial">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Check size min (USD)</label>
                <input
                  type="number"
                  min={0}
                  value={form.check_size_min}
                  onChange={(e) => updateField('check_size_min', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Check size max (USD)</label>
                <input
                  type="number"
                  min={0}
                  value={form.check_size_max}
                  onChange={(e) => updateField('check_size_max', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Estimated AUM (USD)</label>
                <input
                  type="number"
                  min={0}
                  value={form.estimated_aum}
                  onChange={(e) => updateField('estimated_aum', e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Fund size (USD)</label>
                <input
                  type="number"
                  min={0}
                  value={form.fund_size}
                  onChange={(e) => updateField('fund_size', e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>
          </Section>

          {/* DD Profile */}
          <Section title="Due Diligence Profile">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>DD style</label>
                <select
                  value={form.dd_style}
                  onChange={(e) => updateField('dd_style', e.target.value as DDStyle | '')}
                  className={inputClass}
                >
                  <option value="">— Select —</option>
                  {DD_STYLE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Decision timeline</label>
                <select
                  value={form.decision_timeline}
                  onChange={(e) =>
                    updateField('decision_timeline', e.target.value as DecisionTimeline | '')
                  }
                  className={inputClass}
                >
                  <option value="">— Select —</option>
                  {DECISION_TIMELINE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Post-investment involvement</label>
                <select
                  value={form.post_investment_involvement}
                  onChange={(e) =>
                    updateField(
                      'post_investment_involvement',
                      e.target.value as PostInvestmentInvolvement | ''
                    )
                  }
                  className={inputClass}
                >
                  <option value="">— Select —</option>
                  {POST_INVESTMENT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Risk tolerance</label>
                <select
                  value={form.risk_tolerance}
                  onChange={(e) =>
                    updateField('risk_tolerance', e.target.value as RiskTolerance | '')
                  }
                  className={inputClass}
                >
                  <option value="">— Select —</option>
                  {RISK_TOLERANCE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className={labelClass}>Preferred instruments</label>
              <MultiSelectChips
                options={INSTRUMENT_OPTIONS}
                selected={form.preferred_instruments}
                onChange={(next) => updateField('preferred_instruments', next)}
              />
            </div>
          </Section>

          {/* Intelligence */}
          <Section title="Intelligence (manual entry — auto-research coming Phase 2)">
            <div>
              <label className={labelClass}>Investment philosophy</label>
              <textarea
                rows={3}
                value={form.investment_philosophy}
                onChange={(e) => updateField('investment_philosophy', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Known interests</label>
              <textarea
                rows={2}
                value={form.known_interests}
                onChange={(e) => updateField('known_interests', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Known red flags</label>
              <textarea
                rows={2}
                value={form.known_red_flags}
                onChange={(e) => updateField('known_red_flags', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Negotiation style</label>
              <textarea
                rows={2}
                value={form.negotiation_style}
                onChange={(e) => updateField('negotiation_style', e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Sentiment summary</label>
              <textarea
                rows={2}
                value={form.sentiment_summary}
                onChange={(e) => updateField('sentiment_summary', e.target.value)}
                className={inputClass}
              />
            </div>
          </Section>

          {/* Relationship */}
          <Section title="Relationship">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Relationship status</label>
                <select
                  value={form.relationship_status}
                  onChange={(e) =>
                    updateField(
                      'relationship_status',
                      e.target.value as RelationshipStatus | ''
                    )
                  }
                  className={inputClass}
                >
                  <option value="">— Select —</option>
                  {RELATIONSHIP_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Tags (comma-separated)</label>
                <input
                  type="text"
                  placeholder="warm intro, follow-up Q3"
                  value={form.tags_csv}
                  onChange={(e) => updateField('tags_csv', e.target.value)}
                  className={inputClass}
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Notes</label>
              <textarea
                rows={4}
                value={form.notes}
                onChange={(e) => updateField('notes', e.target.value)}
                className={inputClass}
              />
            </div>
          </Section>

          {/* Submit bar */}
          <div className="flex items-center justify-end gap-3 sticky bottom-4">
            <Link
              to={isEditMode && investorId !== null ? `/investors/${investorId}` : '/investors'}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {isEditMode ? 'Save changes' : 'Create investor'}
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default NewInvestorPage;
